import React, { useEffect, useState } from 'react';
import { seVendenPlanes } from '../lib/ventaEnLaApp';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOnboarding } from '../context/OnboardingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { PlanBadge } from './ClientDashboard';
import { planDelCatalogo } from '../lib/planAccess';
import { sinAccesoALaApp } from '../lib/puertasDelCliente';
import DiasQueEntrenas from '../components/DiasQueEntrenas';
import { useCerrarSesion } from '../components/CerrarSesion';
import {
    User, Mail,
    LogOut, Lock, ChevronRight, Crown,
    TrendingUp, Edit2, Camera,
    Compass, SlidersHorizontal, Bot, Search, Pill, ClipboardCheck, MessageCircle,
    Bell, Trophy
} from 'lucide-react';
import { mensajeDeError } from '../lib/mensajeDeError';
import { hoyEnEspana, ahoraDeLaApp } from '../lib/horaEspana';
import { hastaCuandoEntra, loQueQueda } from '../lib/finDelPlan';
import { seLeOfreceMantenimiento, elMotivoTraeAlternativa } from '../lib/salidaAMantenimiento';
import { urlDeFoto } from '../lib/fotoDePerfil';

import { PRIMERA_PANTALLA, RUTA_DE } from '../lib/recorridoGuiado';
// "Mejorar mi plan" OCULTO (petición 2026-07-06): el checkout de upgrade no existe aún
// (pagos reales pospuestos). Poner a true cuando se habiliten pagos.
//
// OJO SI SE REACTIVA (punto 40 del doc del 07-08): el diálogo de abajo tiene el plan y el
// precio CABLEADOS -- "Gold, 149€/ciclo" -- y decide qué enseñar con `profile.plan !== 'gold'`.
// Ese Gold es el legacy, que desde el 31-07 ya no se contrata, y 149€ no es su precio (450-847
// según antigüedad, y de todos modos el que manda es el del contrato de cada cliente, punto
// 36). Antes de encenderlo hay que sacar las opciones del catálogo, como ya hacen la lista de
// clientes y la conversión de leads. Se queda escrito y apagado en vez de borrado porque el
// día que haya checkout de upgrade la maqueta sirve; el aviso es para que no se encienda tal
// cual y se le ofrezca a un cliente un plan que no existe a un precio que no es.
const UPGRADE_PLAN_UI = false;

// Los cuatro grupos de «Avisos y recordatorios» (doc «El día», 31-08). Un rótulo pequeño y
// sus filas debajo: siete interruptores seguidos, sin agrupar, se leen como una lista de la
// compra y no como cuatro decisiones distintas.
const GrupoDeAvisos = ({ titulo, children }) => (
    <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-foreground/40 mb-1">{titulo}</p>
        <div className="divide-y divide-border">{children}</div>
    </div>
);

// Una fila con su interruptor. A nivel de módulo y no dentro de la pantalla por lo de
// siempre: definida dentro, cada render crea un componente nuevo y React remonta la fila en
// vez de actualizarla.
const FilaDeAviso = ({ titulo, ayuda, valor, guardando, onCambiar, testId }) => (
    <div className="flex items-center justify-between gap-4 py-2.5">
        <div className="min-w-0">
            <p className="text-base lg:text-sm text-foreground">{titulo}</p>
            {ayuda && <p className="text-sm lg:text-xs text-foreground/50 mt-0.5">{ayuda}</p>}
        </div>
        <button
            type="button"
            role="switch"
            aria-checked={!!valor}
            aria-label={titulo}
            disabled={guardando}
            onClick={() => onCambiar(!valor)}
            data-testid={testId}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 disabled:opacity-50 ${valor ? 'bg-[#FF671F]' : 'bg-muted'}`}
        >
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${valor ? 'left-6' : 'left-1'}`} />
        </button>
    </div>
);

const ProfilePage = () => {
    const navigate = useNavigate();
    const { user, profile, api, refreshUser, refreshProfile, myPlan, planUnpaid, can, planCatalog, pantalla, actuaSinSerAdmin } = useAuth();
    // «COMPARTIR MIS MENÚS» (artefacto «Compartir los menús», 24-09): lo que dijo en la
    // pregunta del alta, por si quiere cambiar de idea. Sin contestar cuenta como apagado.
    const [guardandoMenus, setGuardandoMenus] = useState(false);
    const cambiarCompartirMenus = async (valor) => {
        setGuardandoMenus(true);
        try {
            await api.put('/menus-compartidos/autorizo', { compartir: valor });
            await refreshProfile();
        } catch {
            toast.error('No se pudo guardar. Prueba otra vez.');
        } finally {
            setGuardandoMenus(false);
        }
    };
    const { startTour, available: recorridoDisponible } = useOnboarding();
    const [editing, setEditing] = useState(false);
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
    // La baja (P56 del doc 23-08): antes era un clic y fuera, sin pregunta ni alternativa.
    // Ahora cada motivo tiene su salida, y el equipo se entera al momento.
    const [bajaAbierta, setBajaAbierta] = useState(false);
    const [motivoBaja, setMotivoBaja] = useState('');
    const [detalleBaja, setDetalleBaja] = useState('');
    const [pidiendoBaja, setPidiendoBaja] = useState(null);   // qué salida está en vuelo
    // LA BAJA RECIÉN PEDIDA (12-10). El perfil tarda unos segundos en volver a leerse, y
    // mientras tanto seguían el enlace "No quiero renovar" y el rótulo "Próxima renovación"
    // encima del aviso de que ya no se le cobra. Se da por pedida en cuanto el servidor
    // contesta que sí; el perfil, cuando llega, dice lo mismo.
    const [bajaRecienPedida, setBajaRecienPedida] = useState(false);

    // El precio de Mantenimiento sale del catálogo, no de un número escrito aquí.
    const mantenimiento = planCatalog?.mantenimiento;
    // Su plan en el catálogo aunque esté sin pagar (`myPlan` va vacío entonces): de él sale
    // si ya está en el plan de salida (`lib/salidaAMantenimiento`).
    const suPlanEnElCatalogo = planDelCatalogo(planCatalog, profile?.plan);

    // Registra la intención (motivo + salida) y avisa al equipo al momento. `salida`:
    // 'baja' (se va de verdad), 'mantenimiento' (sigue barato), 'aplazar' (le escribimos),
    // 'revision' (su entrenador le revisa el plan antes de irse).
    const responderBaja = async (salida) => {
        setPidiendoBaja(salida);
        try {
            const r = await api.post('/billing/no-renovar', {
                motivo: motivoBaja,
                detalle: detalleBaja.trim() || undefined,
                salida,
            });
            if (salida === 'mantenimiento') {
                // La intención ya está registrada; de aquí, directo al pago del plan barato.
                const c = await api.post('/billing/checkout-session', {
                    plan: 'mantenimiento',
                    // A la pantalla de renovación, que es la que confirma el pago al
                    // volver (12-10): `/dashboard?renovado=ok` no lo lee nadie, y sin el
                    // aviso de Stripe el cambio a Mantenimiento se quedaba sin confirmar.
                    success_path: '/renovacion?renovado=ok&salida=salida',
                    cancel_path: '/dashboard/profile',
                });
                if (c.data?.checkout_url) { window.location.href = c.data.checkout_url; return; }
            }
            if (salida === 'baja') setBajaRecienPedida(true);
            toast.success(r.data?.mensaje || 'Hecho. Sigues teniendo acceso hasta el final de tu plan.', { siempre: true });
            setBajaAbierta(false);
            setMotivoBaja('');
            setDetalleBaja('');
            refreshProfile();
        } catch (e) {
            toast.error(mensajeDeError(e, 'No se pudo registrar. Inténtalo en un momento.'));
        } finally {
            setPidiendoBaja(null);
        }
    };
    const [formData, setFormData] = useState({
        name: user?.name || '', phone: user?.phone || '',
        email_contacto: profile?.email_contacto || '',
        email_preferido: profile?.email_preferido || 'contacto',
    });
    const [saving, setSaving] = useState(false);

    // El perfil llega después que el usuario (son dos peticiones), así que los dos correos se
    // siembran cuando aparece. Sin esto, abrir «editar» antes de que cargue enseñaba el campo
    // vacío y guardarlo le borraba el correo de contacto.
    useEffect(() => {
        if (!profile) return;
        setFormData(f => ({
            ...f,
            email_contacto: f.email_contacto || profile.email_contacto || '',
            email_preferido: f.email_preferido || profile.email_preferido || 'contacto',
        }));
    }, [profile]);

    // LA FOTO DE PERFIL (Francisco, 26-09). Se pulsa la foto (o «Cambiar foto») y se elige una
    // del teléfono; el servidor la recorta en cuadrado y la deja pequeña.
    const inputFotoRef = React.useRef(null);
    const [subiendoFoto, setSubiendoFoto] = useState(false);
    const elegirFoto = () => inputFotoRef.current?.click();
    const subirFoto = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        if (!f.type.startsWith('image/')) { toast.error('Elige una foto (JPG o PNG).'); return; }
        setSubiendoFoto(true);
        try {
            const datos = new FormData();
            datos.append('file', f);
            await api.post('/auth/me/foto', datos, { headers: { 'Content-Type': 'multipart/form-data' } });
            await refreshUser();
            toast.success('Foto de perfil actualizada');
        } catch (error) {
            toast.error(mensajeDeError(error, 'No hemos podido subir la foto. Prueba con otra.'));
        } finally {
            setSubiendoFoto(false);
        }
    };
    const quitarFoto = async () => {
        setSubiendoFoto(true);
        try {
            await api.delete('/auth/me/foto');
            await refreshUser();
            toast.success('Foto quitada');
        } catch (error) {
            toast.error(mensajeDeError(error, 'No hemos podido quitar la foto.'));
        } finally {
            setSubiendoFoto(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) { toast.error('El nombre no puede estar vacío'); return; }
        setSaving(true);
        try {
            await api.put('/auth/me', { name: formData.name.trim(), phone: formData.phone });
            // El email ya no se cambia desde aquí (29-10): ni el de acceso ni el de contacto.
            await refreshUser();
            if (refreshProfile) await refreshProfile();
            toast.success('Perfil actualizado');
            setEditing(false);
        } catch (error) {
            toast.error(mensajeDeError(error, 'Error al actualizar el perfil'));
        } finally {
            setSaving(false);
        }
    };

    // CAMBIO DE CONTRASEÑA POR ENLACE (Francisco, 23-10: «te manda un link al email y ya
    // funciona; quitar "Contraseña actual"»). Es el mismo correo que «He olvidado mi
    // contraseña» (`/auth/forgot-password`), que va al correo de ACCESO y vale dos horas.
    // Sin pedir la actual y sin dejar cambiarla aquí mismo: con la sesión abierta en un
    // móvil prestado bastaría para quedarse con la cuenta. El enlace exige su correo.
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [enviandoEnlace, setEnviandoEnlace] = useState(false);
    const [enlaceEnviado, setEnlaceEnviado] = useState(false);
    const handleChangePassword = async () => {
        setEnviandoEnlace(true);
        try {
            await api.post('/auth/forgot-password', { email: user?.email });
            setEnlaceEnviado(true);
        } catch (error) {
            toast.error(mensajeDeError(error, 'No se pudo enviar el enlace. Prueba en un momento.'));
        } finally {
            setEnviandoEnlace(false);
        }
    };

    // Preferencias de avisos. El fallo se traga a propósito: no poder leerlas no es algo
    // que el cliente tenga que arreglar, y la fila simplemente no aparece.
    const [prefAvisos, setPrefAvisos] = useState(null);
    const [guardandoPref, setGuardandoPref] = useState(false);
    useEffect(() => {
        let vivo = true;
        api.get('/notifications/preferencias')
            .then(({ data }) => { if (vivo) setPrefAvisos(data); })
            .catch((e) => console.warn('No se han podido leer las preferencias de avisos', e));
        return () => { vivo = false; };
    }, [api]);

    // Vale para las dos preferencias que quedan (17-10). Responde al toque y no al
    // servidor: si el guardado falla, vuelve a como estaba y se dice.
    const guardarPref = async (cambio) => {
        const antes = prefAvisos;
        setPrefAvisos({ ...prefAvisos, ...cambio });
        setGuardandoPref(true);
        try {
            const { data } = await api.put('/notifications/preferencias', cambio);
            setPrefAvisos(data);
        } catch (error) {
            setPrefAvisos(antes);
            console.error('Error al guardar la preferencia de avisos', error);
            toast.error('No se ha podido guardar. Inténtalo otra vez.');
        } finally {
            setGuardandoPref(false);
        }
    };

    // El mismo gesto que la salida del cuestionario y de Planes (`components/CerrarSesion`).
    const handleLogout = useCerrarSesion();


    // SIN FECHA DE RENOVACIÓN NO SE ENSEÑA LA ETIQUETA.
    // Ponía «RENOVACIÓN · No definida», que es un hueco de la base de datos en la cara de
    // alguien que paga 149 €. Jesús, 11-08: «si hay fecha, se pone la fecha; si no la hay, no
    // se enseña la etiqueta». Callar dice menos que decir que no se sabe.
    //
    // Y SOLO LAS FECHAS QUE EL SERVIDOR DA POR BUENAS (P50 del doc 23-08): `renovacion.*`
    // llega ya filtrado (una «renovación» a años vista es una membresía importada de Calma,
    // no una renovación). El `next_payment` crudo del perfil ya no entra en la cascada,
    // porque era justo por donde se colaba el «1 de febrero de 2030».
    const noRenueva = !!profile?.no_renovar || bajaRecienPedida;
    // A QUIEN NO TIENE ACCESO NO SE LE PROMETEN DÍAS NI SE LE OFRECE LA BAJA (14-10, BAJ-02).
    // Al cliente que el equipo desactivó, el Inicio le decía "Tu suscripción ha terminado" y
    // esta pantalla "Te quedan 95 días", con el "No quiero renovar" prometiéndole acceso
    // hasta el final de su plan. Quién está sin acceso lo dice la misma regla que cierra el
    // menú (`lib/puertasDelCliente`); sin perfil cargado no se decide nada.
    const sinAcceso = sinAccesoALaApp({ profile, perfilNoCargado: !profile });
    // Quien pidió la baja ve el último día que entra (`acceso_hasta`, el mismo del correo al
    // equipo y de la frase de la baja), no el día del cobro (hoja del 22-10, fila 78).
    const fechaDeRenovacion = (noRenueva && profile?.renovacion?.acceso_hasta)
        || profile?.renovacion?.fecha
        || profile?.renovacion?.proximo_cobro
        || null;
    // ¿SU PLAN VA POR CICLOS? Lo dice el servidor, que lo saca del catálogo: los planes
    // mensuales (El Lunes Empiezo, Mantenimiento, Calculadora JP) no llevan rutina y por
    // tanto no tienen ciclo de cuatro semanas. Por defecto sí, que es lo que vale para los
    // planes de siempre y para un perfil que llegue sin la marca.
    const llevaCiclo = profile?.lleva_ciclo !== false;

    // Los días hasta esa fecha, contados con el día de España (punto 93): fechas sin hora,
    // restadas como días enteros, para que no dependan de la zona del aparato.
    const diasDeCiclo = (() => {
        if (!fechaDeRenovacion) return null;
        const [a, m, d] = String(fechaDeRenovacion).slice(0, 10).split('-').map(Number);
        const [ha, hm, hd] = hoyEnEspana().split('-').map(Number);
        if (!a || !m || !d) return null;
        const dias = Math.round((Date.UTC(a, m - 1, d) - Date.UTC(ha, hm - 1, hd)) / 86400000);
        return dias >= 0 ? dias : null;
    })();

    // LA COMPROBACIÓN DE SENTIDO COMÚN DE JESÚS (8-09, punto 3 del bloque B).
    //
    // La semana sale del ancla del ciclo y los días que quedan salen de la fecha de cobro,
    // y nadie los cruza: por eso hay fichas que dicen «semana 10 de 12» y «te queda 1 día»
    // a la vez. Si no se hablan, se enseña el día -- que el cliente puede comprobar contra
    // su cobro -- y la semana se calla, en vez de dejar las dos mintiendo a la vez.
    //
    // El margen es de una semana: entre el alta, la renovación y el día de España puede
    // bailar algún día sin que nada esté mal.
    const semanaYDiasSeHablan = (() => {
        const semana = profile?.week;
        const total = profile?.cycle_total_weeks;
        if (!semana || !total || diasDeCiclo == null) return true;   // sin los dos, nada que cruzar
        return Math.abs(diasDeCiclo - (total - semana) * 7) <= 7;
    })();

    // La fecha se pinta como dia LOCAL, no pasando el ISO a ahoraDeLaApp(): '2026-09-22' se
    // interpreta como medianoche UTC y en husos por detras de Greenwich salia «21 de
    // septiembre», un dia antes del real (regla de la casa: el dia es el del navegador).
    const pintaFecha = (iso, opciones) => {
        const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
        if (!a || !m || !d) return '';
        return new Date(a, m - 1, d).toLocaleDateString('es-ES', opciones);
    };

    return (
        <div className="p-4 md:p-6 pb-24 md:pb-6 animate-fade-in bg-background min-h-screen relative overflow-hidden">
            <div className="relative z-10 space-y-6 max-w-lg mx-auto">
                <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Barlow Condensed' }} data-testid="profile-heading">MI PERFIL</h1>

                {/* AQUÍ IBA «MACROS PROVISIONALES» (punto 111 del 25-08), con lo que le faltaba
                    de la ficha y «Completar mis datos». Fuera por Enrique, 29-10. */}

                {/* Profile Card */}
                <Card className="bg-card border-border">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                {/* SU INICIAL, NO UN MUÑECO (punto 4.18). Salía un dibujo de
                                    mujer para todo el mundo: el avatar lo generaba
                                    api.dicebear.com a partir de una semilla, y el dibujo que
                                    toque es cosa suya.
                                    Y había algo peor que el dibujo: la semilla era EL CORREO
                                    DEL CLIENTE, así que cada vez que alguien abría su perfil
                                    se mandaba su dirección a un servicio de fuera. Para un
                                    avatar por defecto. Con la inicial no sale nada de aquí, no
                                    depende de que un tercero esté vivo y no hay que pedir
                                    permiso a nadie. */}
                                {/* Y AHORA SU FOTO, SI LA PONE (26-09): se pulsa y se cambia. */}
                                <button type="button" onClick={elegirFoto} disabled={subiendoFoto}
                                    aria-label="Cambiar la foto de perfil" data-testid="foto-perfil"
                                    className="relative block rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand">
                                    <Avatar className={`w-16 h-16 border-2 border-[#FF671F] ${subiendoFoto ? 'opacity-50' : ''}`}>
                                        {urlDeFoto(user) && <AvatarImage src={urlDeFoto(user)} alt="" className="object-cover" />}
                                        <AvatarFallback className="text-xl bg-[#FF671F] text-white">
                                            {user?.name?.charAt(0)?.toUpperCase() || '?'}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center">
                                        <Camera className="w-3.5 h-3.5 text-foreground" />
                                    </span>
                                </button>
                                <input ref={inputFotoRef} type="file" accept="image/*" className="hidden"
                                    onChange={subirFoto} data-testid="foto-perfil-input" />
                            </div>
                            {/* `min-w-0` y `truncate`, que si no el correo manda.
                                Un flex hijo no encoge por debajo de su contenido salvo que se
                                le diga, así que «clientedemo@test.com» fijaba el ancho de esta
                                columna y empujaba el lápiz de editar FUERA de la pantalla: a
                                320 px el botón no existía para el cliente. Medido el 10-08. */}
                            <div className="flex-1 min-w-0">
                                <h2 className="font-bold text-foreground text-lg leading-tight break-words">{user?.name?.toUpperCase()}</h2>
                                <p className="text-foreground/50 text-sm truncate">{user?.email}</p>
                                {profile && <div className="mt-1"><PlanBadge plan={planUnpaid ? null : profile.plan} /></div>}
                            </div>
                            {/* «EDITAR», CON SU PALABRA (26-09): el lápiz solo no se veía como
                                la puerta a cambiar el nombre. */}
                            <Button
                                variant="outline" size="sm"
                                className="bg-transparent border-input text-foreground hover:text-brand hover:border-brand/50 flex-shrink-0 gap-1.5"
                                onClick={() => setEditing(!editing)}
                                data-testid="edit-profile-btn"
                            >
                                <Edit2 className="w-3.5 h-3.5" /> <span className="hidden min-[360px]:inline">{editing ? 'Cerrar' : 'Editar'}</span>
                            </Button>
                        </div>
                        {editing && (
                            <div className="mt-5 space-y-4">
                                <Separator className="bg-border" />
                                <div className="grid gap-3">
                                    <div>
                                        <Label className="text-foreground/70 text-xs uppercase tracking-wider">Foto</Label>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            <Button type="button" variant="outline" size="sm" onClick={elegirFoto} disabled={subiendoFoto}
                                                data-testid="foto-cambiar"
                                                className="bg-transparent border-input text-foreground gap-1.5">
                                                <Camera className="w-3.5 h-3.5" /> {user?.foto ? 'Cambiar foto' : 'Poner foto'}
                                            </Button>
                                            {user?.foto && (
                                                <Button type="button" variant="ghost" size="sm" onClick={quitarFoto} disabled={subiendoFoto}
                                                    data-testid="foto-quitar" className="text-muted-foreground hover:text-red-500">
                                                    Quitar foto
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-foreground/70 text-xs uppercase tracking-wider">Nombre</Label>
                                        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-background border-input text-foreground mt-1" />
                                    </div>
                                    <div>
                                        <Label className="text-foreground/70 text-xs uppercase tracking-wider">Teléfono</Label>
                                        <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+34 612 345 678" className="bg-background border-input text-foreground mt-1" />
                                    </div>
                                    {/* SIN «EMAIL DE CONTACTO» NI «¿A CUÁL TE ESCRIBIMOS?» (Enrique, 29-10): el
                                        cliente ya no cambia su email desde el perfil. */}
                                </div>
                                {!actuaSinSerAdmin && (
                                <button type="button" onClick={() => setShowPasswordDialog(true)}
                                    data-testid="editar-contrasena"
                                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-input text-sm text-foreground hover:border-brand/50">
                                    <span className="flex items-center gap-2"><Lock className="w-4 h-4 text-muted-foreground" /> Cambiar contraseña</span>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                </button>
                                )}
                                <div className="flex gap-2 justify-end">
                                    <Button variant="outline" onClick={() => setEditing(false)} className="bg-transparent border-input text-foreground hover:border-white/50 text-sm">Cancelar</Button>
                                    <Button onClick={handleSave} disabled={saving} className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white text-sm">{saving ? 'Guardando...' : 'Guardar'}</Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* SIN PLAN: checkout a medias O cuenta recién creada que volvió atrás
                    desde /planes (bug del 20-08: no tenía ningún camino para volver a
                    verlos). La misma tarjeta para los dos, con el texto de su caso. */}
                {profile && (planUnpaid || !profile.plan) && (
                    <Card className="bg-card border-[#FF671F]/30">
                        <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex-1">
                                <p className="font-bold text-foreground uppercase tracking-wider text-sm mb-1">Sin plan activo</p>
                                <p className="text-sm text-foreground/60">
                                    {!seVendenPlanes()
                                        ? 'Todavía no tienes un plan activo. Si tienes cualquier duda, escríbenos por el chat.'
                                        : planUnpaid
                                        ? 'El pago de tu plan no llegó a completarse. Elige un plan para terminar la compra.'
                                        : 'Todavía no tienes plan. Mira los planes y elige el tuyo para desbloquear la app entera.'}
                                </p>
                            </div>
                            {/* Con los planes cerrados (15-10, `lib/ventaEnLaApp`), sin botón a ellos. */}
                            {seVendenPlanes() && (
                            <Button className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold uppercase tracking-wider"
                                onClick={() => navigate('/planes')} data-testid="finish-checkout-btn">
                                {planUnpaid ? 'Elegir plan' : 'Ver los planes'}
                            </Button>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Plan Info. Sin plan no hay tarjeta de plan: aquí vivía el bug del
                    «No quiero renovar» saliéndole a quien no tiene nada que renovar. */}
                {profile && !planUnpaid && profile.plan && (
                    <Card className="bg-gradient-to-br from-[#FF671F]/10 to-[#FF671F]/5 border-[#FF671F]/30">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center justify-between">
                                <span className="flex items-center gap-2 text-foreground uppercase tracking-wider text-base">
                                    <Crown className="w-5 h-5 text-[#FF671F]" />
                                    Mi Plan
                                </span>
                                <PlanBadge plan={profile.plan} planName={myPlan?.name} />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-foreground/50 uppercase tracking-wider">Precio</p>
                                    {/* EL PRECIO LO RESUELVE EL SERVIDOR (punto 2.4c). Aquí se
                                        pintaba `profile.price` en crudo, y ese campo llegó a cero
                                        en los 168 perfiles que vinieron de Calma: a un cliente de
                                        pago, sin cortesía marcada, se le decía «0 €/ciclo». Y es
                                        lo primero que ve quien recibe acceso. */}
                                    <p className="text-3xl font-bold text-[#FF671F]" style={{ fontFamily: 'Barlow Condensed' }}>
                                        {profile.precio_cortesia
                                            ? <>Cortesía</>
                                            : <>{profile.precio_ciclo ? `${profile.precio_ciclo}€` : '-'}<span className="text-sm font-normal text-foreground/50">
                                                {/* «/mes» o «/ciclo» lo dice el catálogo por el
                                                    ciclo del plan (P51): El Lunes Empiezo y
                                                    Mantenimiento son mensuales y aquí salían
                                                    como «/ciclo». */}
                                                /{profile.precio_periodo === 'mes' ? 'mes' : 'ciclo'}</span></>}
                                    </p>
                                    {/* La nota de precio del catálogo (un rango general del plan) solo se
                                        muestra si el cliente no tiene un precio propio, para no dar dos cifras
                                        contradictorias (p.ej. "149€/ciclo" y "450-847€/trimestre"). */}
                                    {!profile.precio_cortesia && !profile.precio_ciclo && myPlan?.precio_nota && (
                                        <p className="text-xs text-foreground/50 mt-1">{myPlan.precio_nota}</p>
                                    )}
                                </div>
                                {/* Punto 2.4d: aquí ponía «No definida» a un cliente cuya
                                    membresía había vencido una semana antes, porque solo miraba
                                    `next_payment` -- el próximo COBRO --, y los migrados de
                                    Calma no tienen ninguno. Ahora se dice lo que se sabe, si ya
                                    pasó se dice que pasó, y si no hay fecha no se enseña nada.
                                    «Próxima renovación» ocupa dos líneas en 390 px y descuadra
                                    la columna frente al precio: en el teléfono, «Renovación». */}
                                {fechaDeRenovacion && (
                                    <div>
                                        <p className="text-sm text-foreground/50 uppercase tracking-wider">
                                            {/* Quien ya dijo que no renueva no tiene "próxima
                                                renovación": esa fecha es hasta cuándo tiene
                                                acceso (12-10). */}
                                            {profile.renovacion?.vencida ? 'Tu plan venció'
                                                : noRenueva ? 'Tienes acceso hasta' : (
                                                <>
                                                    <span className="lg:hidden">Renovación</span>
                                                    <span className="hidden lg:inline">Próxima renovación</span>
                                                </>
                                            )}
                                        </p>
                                        <p className={`font-semibold text-sm ${profile.renovacion?.vencida ? 'text-amber-500' : 'text-foreground'}`}
                                            data-testid="perfil-renovacion">
                                            {pintaFecha(fechaDeRenovacion, { day: 'numeric', month: 'long', year: 'numeric' })}
                                        </p>
                                        {/* LOS DÍAS QUE QUEDAN, DE LA MISMA FECHA (Gonzalo, 4-09,
                                            punto 93). El aviso de la campana, esta fecha y «la
                                            semana X de Y» salen de tres sitios y no cuadraban.
                                            Aquí se cuenta desde ESTA fecha con el día de España,
                                            que es con el que cuenta el aviso: el cliente ve el
                                            mismo número en los dos sitios. */}
                                        {/* Y SI LOS DOS NÚMEROS NO SE HABLAN, NO SE ENSEÑAN LOS DOS
                                            (10-09, punto 3 del bloque B de Jesús del 8-09).

                                            Su comprobación, literal: «si va por la semana N de
                                            12, los días que quedan tienen que ser (12−N)×7
                                            aproximadamente. Si no cuadra, no se enseñan los
                                            dos». Y con razón: en la cuenta de Juan Montalvo la
                                            app decía «semana 10 de 12» y «te queda 1 día de
                                            ciclo» a la vez, que es imposible y el cliente lo ve.

                                            La causa de fondo es que la semana sale del ancla del
                                            ciclo y los días del cobro, y nadie los cruza. Hasta
                                            que sean el mismo dato, esto evita que la
                                            contradicción llegue a pantalla: manda el día, que es
                                            el que el cliente puede comprobar contra su cobro, y
                                            la semana se calla. Margen de una semana, que es lo
                                            que puede bailar entre el alta y la renovación. */}
                                        {/* Y NO EN LOS PLANES QUE NO VAN POR CICLOS (Cesar,
                                            19-09): «esto no va para el ELM». El Lunes
                                            Empiezo, Mantenimiento y Calculadora JP se pagan
                                            cada mes y no llevan rutina, asi que no tienen
                                            ciclo de cuatro semanas: lo suyo es la fecha de
                                            pago, que ya esta ahi arriba en «Proxima
                                            renovacion». Lo dice el servidor con `lleva_ciclo`,
                                            que sale del catalogo. */}
                                        {/* LA MISMA CUENTA Y LAS MISMAS PALABRAS QUE LA RENOVACIÓN
                                            (12-10). Aquí se contaba hasta la fecha de arriba y la
                                            renovación hasta el domingo de esa semana, que es el
                                            último día con acceso: "88 días" en una pantalla y "90
                                            días, hasta el domingo 10 de enero" en la otra. Si el
                                            servidor manda esa cuenta (`renovacion.ultimo_dia`),
                                            se escribe esa, con `lib/finDelPlan`; la de antes
                                            queda solo para quien no tiene fecha de fin guardada. */}
                                        {sinAcceso ? null : llevaCiclo && loQueQueda(profile.renovacion) ? (
                                            <p className="text-xs text-foreground/50 mt-0.5" data-testid="perfil-dias-de-ciclo">
                                                {loQueQueda(profile.renovacion)}
                                            </p>
                                        ) : llevaCiclo && diasDeCiclo != null && !profile.renovacion?.vencida && (
                                            <p className="text-xs text-foreground/50 mt-0.5" data-testid="perfil-dias-de-ciclo">
                                                {diasDeCiclo === 0 ? 'Tu ciclo acaba hoy'
                                                    : diasDeCiclo === 1 ? 'Te queda 1 día de ciclo'
                                                        : `Te quedan ${diasDeCiclo} días de ciclo`}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* «Estás en la semana 7» (la pantalla Mi plan del doc 19-08).
                                LAS DOS MITADES DE LA FRASE, DEL MISMO SITIO (24-08). El «de
                                12» salía del catálogo (`myPlan`) y la semana la cuenta el
                                servidor con core/cycle, que busca el plan sin resolver
                                alias: a un perfil migrado escrito «CalMa» no le encontraba
                                el ciclo, así que la semana crecía sin dar la vuelta y aquí
                                se leía «Estás en la semana 63 de 12». `cycle_total_weeks`
                                es el total con el que se calculó ESA semana, así que los dos
                                números no pueden contradecirse. */}
                            {/* CON `!= null`, QUE EL 0 ES UN NUMERO (Francisco, 11-09). Con
                                `profile.week &&` pasaban las dos cosas malas a la vez: la
                                frase no salia en la Semana 0 y, como en React una expresion
                                `0 && ...` vale 0, **se pintaba un «0» suelto** debajo de los
                                dias de ciclo. Su recado, literal: «0 esto esta mal, deberia
                                decir semana 0 de...». */}
                            {llevaCiclo && profile.week != null && semanaYDiasSeHablan && (
                                <p className="text-sm text-foreground/60" data-testid="perfil-semana">
                                    Estás en la semana {profile.week}
                                    {profile.cycle_total_weeks ? ` de ${profile.cycle_total_weeks}` : ''}
                                </p>
                            )}
                            {/* AQUÍ IBA «TU PLAN INCLUYE» con sus líneas, y se quita (Francisco,
                                23-10). */}
                            {UPGRADE_PLAN_UI && profile.plan !== 'gold' && (
                                <Button
                                    className="w-full bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold uppercase tracking-wider"
                                    onClick={() => setShowUpgradeDialog(true)}
                                    data-testid="upgrade-btn"
                                >
                                    <TrendingUp className="w-4 h-4 mr-2" /> Mejorar mi plan
                                </Button>
                            )}

                            {/* Aquí vivía el «No quiero renovar». Se va abajo del todo, junto
                                a «Cerrar sesión» (Jesús, punto 91): dentro de esta tarjeta se
                                leía pegado al precio y a la renovación. */}
                        </CardContent>
                    </Card>
                )}

                {/* LA BAJA CON SUS CUATRO MOTIVOS Y UNA SALIDA PARA CADA UNO (P56 del doc
                    23-08). Antes era un clic y fuera: sin pregunta, sin alternativa y sin
                    que el equipo se enterase. Ahora el motivo abre su salida (Mantenimiento
                    si es caro, aplazar si no hay tiempo, revisión del plan si no ve
                    resultados, texto libre si es otra cosa) y TODO lo que se confirme queda
                    registrado y avisa al equipo al momento. */}
                <Dialog open={bajaAbierta} onOpenChange={(o) => { setBajaAbierta(o); if (!o) { setMotivoBaja(''); setDetalleBaja(''); } }}>
                    <DialogContent className="bg-card border-border max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-foreground">¿Por qué no quieres renovar?</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-muted-foreground -mt-1">
                            Pase lo que pase, sigues teniendo acceso hasta el final de tu plan
                            {/* El último día con acceso, el mismo que dicen Mi perfil y la
                                renovación (`lib/finDelPlan`); sin él, la fecha de antes. */}
                            {hastaCuandoEntra(profile?.renovacion) ? ` (${hastaCuandoEntra(profile.renovacion)})`
                                : fechaDeRenovacion ? ` (el ${pintaFecha(fechaDeRenovacion, { day: 'numeric', month: 'long' })})` : ''}.
                        </p>
                        <div className="space-y-2">
                            {[['caro', 'Es caro'],
                              ['sin_tiempo', 'No tengo tiempo ahora'],
                              ['sin_resultados', 'No estoy viendo resultados'],
                              ['otra', 'Otra cosa']].map(([clave, texto]) => (
                                <button key={clave} type="button"
                                    onClick={() => { setMotivoBaja(clave); setDetalleBaja(''); }}
                                    data-testid={`baja-motivo-${clave}`}
                                    className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                                        motivoBaja === clave
                                            ? 'border-[#FF671F] bg-[#FF671F]/10 text-foreground'
                                            : 'border-border text-foreground/70 hover:border-foreground/30'}`}>
                                    {texto}
                                </button>
                            ))}
                        </div>

                        {/* La salida de cada motivo. */}
                        {/* A quien ya está en Mantenimiento no se le ofrece (14-10,
                            `lib/salidaAMantenimiento`): le proponía pagar otra vez lo suyo. */}
                        {motivoBaja === 'caro' && seLeOfreceMantenimiento(suPlanEnElCatalogo) && (
                            <div className="rounded-lg border border-[#FF671F]/30 bg-[#FF671F]/5 p-4 space-y-3" data-testid="baja-salida-caro">
                                <p className="text-sm text-foreground/80">
                                    Antes de irte: tienes <b>Mantenimiento</b>
                                    {mantenimiento?.precio ? <> por <b>{Math.round(mantenimiento.precio)} €/mes</b></> : null}.
                                    Te quedas con la app, tus números y tus datos, y puedes volver
                                    al programa cuando quieras.
                                </p>
                                <Button onClick={() => responderBaja('mantenimiento')} disabled={!!pidiendoBaja}
                                    data-testid="baja-salida-mantenimiento"
                                    className="w-full bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold">
                                    {pidiendoBaja === 'mantenimiento' ? 'Un momento…' : 'Pasarme a Mantenimiento'}
                                </Button>
                            </div>
                        )}
                        {motivoBaja === 'sin_tiempo' && (
                            <div className="rounded-lg border border-[#FF671F]/30 bg-[#FF671F]/5 p-4 space-y-3" data-testid="baja-salida-sin-tiempo">
                                <p className="text-sm text-foreground/80">
                                    No hace falta decidirlo hoy. Lo aplazamos, te escribimos
                                    nosotros en unos días y lo ves con calma.
                                </p>
                                <Button onClick={() => responderBaja('aplazar')} disabled={!!pidiendoBaja}
                                    data-testid="baja-salida-aplazar"
                                    className="w-full bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold">
                                    {pidiendoBaja === 'aplazar' ? 'Un momento…' : 'Aplazarlo, escribidme'}
                                </Button>
                            </div>
                        )}
                        {motivoBaja === 'sin_resultados' && (
                            <div className="rounded-lg border border-[#FF671F]/30 bg-[#FF671F]/5 p-4 space-y-3" data-testid="baja-salida-sin-resultados">
                                <p className="text-sm text-foreground/80">
                                    Antes de irte, {profile?.entrenador?.nombre ? 'tu entrenador' : 'el equipo'} puede
                                    revisar tu plan y proponerte cambios. Le avisamos ahora mismo.
                                </p>
                                <Button onClick={() => responderBaja('revision')} disabled={!!pidiendoBaja}
                                    data-testid="baja-salida-revision"
                                    className="w-full bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold">
                                    {pidiendoBaja === 'revision' ? 'Un momento…' : 'Que revisen mi plan'}
                                </Button>
                            </div>
                        )}
                        {motivoBaja === 'otra' && (
                            <div className="space-y-2" data-testid="baja-salida-otra">
                                <Label className="text-foreground/70 text-xs uppercase tracking-wider">Cuéntanos qué pasa</Label>
                                <textarea value={detalleBaja} onChange={(e) => setDetalleBaja(e.target.value)}
                                    rows={3} maxLength={500}
                                    data-testid="baja-detalle"
                                    placeholder="Con tus palabras: nos llega al equipo tal cual"
                                    className="w-full rounded-lg bg-background border border-input text-foreground text-sm p-3 resize-none focus:outline-none focus:border-[#FF671F]" />
                            </div>
                        )}

                        <DialogFooter>
                            <Button onClick={() => responderBaja('baja')}
                                disabled={!motivoBaja || !!pidiendoBaja || (motivoBaja === 'otra' && !detalleBaja.trim())}
                                data-testid="baja-confirmar"
                                variant={elMotivoTraeAlternativa(motivoBaja, suPlanEnElCatalogo) ? 'outline' : 'default'}
                                className={elMotivoTraeAlternativa(motivoBaja, suPlanEnElCatalogo)
                                    ? 'bg-transparent border-input text-foreground/70 hover:text-foreground'
                                    : 'bg-[#FF671F] hover:bg-[#FF671F]/90 text-white font-bold'}>
                                {pidiendoBaja === 'baja' ? 'Un momento…'
                                    : elMotivoTraeAlternativa(motivoBaja, suPlanEnElCatalogo) ? 'Seguir con la baja' : 'Confirmar la baja'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* TODO LO DEMÁS, CON SU NOMBRE.
                    La barra de abajo bajó a cuatro (Inicio · Nutrición · Seguimiento ·
                    Perfil) y de Inicio se fueron los ocho accesos rápidos. Lo que dejó de
                    tener puerta aterriza aquí, que es donde el propio documento pone «Mis
                    macros»: «de la pestaña Perfil, abajo a la derecha».
                    Cada entrada respeta las capacidades del plan, igual que antes: al que
                    no tiene suplementación no se le enseña Suplementos, y al plan sin
                    entrenador no se le enseña el Chat.
                    Y sin acceso no se enseña ninguna (BAJ-02): todas devuelven al Inicio. */}
                <Card className={`bg-card border-border ${sinAcceso ? 'hidden' : 'lg:hidden'}`}>
                    <CardContent className="p-0">
                        {[
                            { icon: SlidersHorizontal, title: 'Mis macros', path: '/dashboard/macro-calculator' },
                            // El asistente solo si su interruptor está encendido, y nace
                            // apagado (Francisco, 26-08). Ver ClientDashboard.
                            pantalla('t7_asistente') && { icon: Bot, title: 'Asistente IA', path: '/dashboard/chatbot' },
                            { icon: Search, title: 'Alimentos', path: '/dashboard/foods' },
                            // EL RANKING, ENTRE MIS MACROS Y SUPLEMENTOS (artefacto «Compartir
                            // los menús», 24-09).
                            { icon: Trophy, title: 'Ranking', path: '/dashboard/ranking' },
                            can('suplementacion') && { icon: Pill, title: 'Suplementos', path: '/dashboard/supplements' },
                            // Aquí había una fila de «Check-ins». Se va: desde el 10-08 la
                            // puerta de eso es Seguimiento, que está en la barra de abajo, y
                            // dentro tiene su tarjeta de «Hoy». Dos puertas a lo mismo, una de
                            // ellas con el nombre viejo, es justo lo que había que quitar.
                            can('chat') && { icon: MessageCircle, title: 'Chat', path: '/dashboard/messages' },
                        ].filter(Boolean).map((item, i) => (
                            <React.Fragment key={item.title}>
                                {i > 0 && <Separator className="bg-border" />}
                                <button
                                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                                    onClick={() => navigate(item.path)}
                                    data-testid={`ir-a-${item.path.split('/').pop()}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                                            <item.icon className="w-5 h-5 text-[#FF671F]" />
                                        </div>
                                        <div className="text-left">
                                            {/* En el teléfono el texto sube un escalón: estas
                                                filas son ahora la puerta de media app y se leían
                                                en 14 y 12 px. En escritorio se quedan igual. */}
                                            <p className="font-medium text-foreground text-base lg:text-sm">{item.title}</p>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-foreground/30" />
                                </button>
                            </React.Fragment>
                        ))}
                    </CardContent>
                </Card>

                {/* LOS DÍAS QUE ENTRENA (ficha 06 del doc de Jesús del 5-09).
                    «Que se le pregunte al cliente qué días entrena: los siete días
                    marcables, y él los cambia cuando quiera.»

                    Y VIVE AQUÍ porque aquí es donde vive el dato. Es un dato suyo y de su
                    vida, no de un día: lo contesta en el alta y hasta hoy no había ninguna
                    pantalla donde tocarlo, así que el que cambiaba de turno o de gimnasio se
                    quedaba con la semana de cuando se dio de alta. Mi perfil es donde ya
                    están sus otras respuestas permanentes, y es lo que dice el propio
                    enunciado del alta: «después lo podrás cambiar si quieres».

                    Y ADEMÁS SE OFRECE EN MI SEMANA, que es donde se echa en falta: allí sale
                    solo cuando hay días «Por decidir». Es el mismo trozo de pantalla en los
                    dos sitios (`components/DiasQueEntrenas`), para que no haya dos formas de
                    contestar la misma pregunta. */}
                <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-foreground text-base">DÍAS DE ENTRENAMIENTO</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                        <p className="text-sm text-foreground/60 mb-3">
                            Marca los días que tienes previsto entrenar.
                        </p>
                        <DiasQueEntrenas marcados={profile?.training_weekdays} />
                    </CardContent>
                </Card>

                {/* PREFERENCIAS (17-10). Aquí estaba «AVISOS Y RECORDATORIOS», con los
                    interruptores de cada aviso, y se quitó entera: la campana solo lleva ya
                    los avisos que no se apagan (`core/avisos_cliente.AVISOS_DE_LA_CAMPANA`).
                    Quedan las tres cosas que NO son avisos: rellenar el cierre del día,
                    valorar la semana y compartir los menús. Lo que cada cliente tuviera
                    guardado de los demás interruptores se queda en su ficha sin leerse. */}
                {prefAvisos && (
                    <Card className="bg-card border-border">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-foreground text-base">PREFERENCIAS</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-2 space-y-5">
                            {/* «Rellenar el cierre del día» se quitó el 25-10: el check-in
                                diario sale siempre en Mi día, para todos. */}
                            {/* LA VALORACIÓN DE LA SEMANA (ficha 17 de «Mi semana»): «un
                                interruptor para apagarla del todo si no la quiere». */}
                            <GrupoDeAvisos titulo="Tu semana">
                                <FilaDeAviso
                                    titulo="Valorar tu semana"
                                    testId="aviso-valoracion-semana"
                                    valor={prefAvisos.valoracion_semana}
                                    guardando={guardandoPref}
                                    onCambiar={(v) => guardarPref({ valoracion_semana: v })} />
                            </GrupoDeAvisos>

                            {/* ── Tus menús ───────────────────────────────────────────
                                «El único sitio donde el cliente gestiona esto.» La frase de
                                debajo «es la que sostiene el trato»: sin la segunda mitad,
                                todo el mundo lo apaga. */}
                            <GrupoDeAvisos titulo="Tus menús">
                                <FilaDeAviso
                                    titulo="Compartir mis menús"
                                    ayuda="Las comidas de tus días cuadrados se comparten solas en el ranking; encendido, salen con tu nombre. Si lo apagas, dejas de compartir y dejas de ver los de los demás."
                                    testId="compartir-menus"
                                    valor={profile?.compartir_menus === true}
                                    guardando={guardandoMenus}
                                    onCambiar={cambiarCompartirMenus} />
                            </GrupoDeAvisos>
                        </CardContent>
                    </Card>
                )}

                {/* Settings */}
                <Card className="bg-card border-border">
                    <CardContent className="p-0">
                        {[
                            // «Cambiar contraseña» ya no va aquí (Francisco, 23-10: salía dos
                            // veces): se queda solo en «Editar», dentro de Mi perfil.
                            // El recorrido de la primera vez (doc 21-08, apartado 23): quien
                            // lo saltó no lo vuelve a ver solo, así que esta fila es su única
                            // puerta de vuelta. En el teléfono es una fila más de ajustes; en
                            // escritorio sigue siendo el botón de debajo.
                            ...(recorridoDisponible ? [{
                                icon: Compass, title: 'Ver el recorrido',
                                onClick: () => { navigate(RUTA_DE[PRIMERA_PANTALLA]); startTour(); },
                            }] : []),
                        ].map((item, i) => (
                            <React.Fragment key={item.title}>
                                {i > 0 && <Separator className={`bg-border ${item.soloMovil ? 'lg:hidden' : ''}`} />}
                                <button
                                    className={`w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors ${item.soloMovil ? 'lg:hidden' : ''}`}
                                    onClick={item.onClick}
                                    data-testid={`setting-${i}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                                            <item.icon className="w-5 h-5 text-[#FF671F]" />
                                        </div>
                                        <div className="text-left">
                                            {/* En el teléfono el texto sube un escalón: estas
                                                filas son ahora la puerta de media app y se leían
                                                en 14 y 12 px. En escritorio se quedan igual. */}
                                            <p className="font-medium text-foreground text-base lg:text-sm">{item.title}</p>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-foreground/30" />
                                </button>
                            </React.Fragment>
                        ))}
                    </CardContent>
                </Card>

                {/* Logout */}
                <Button
                    variant="outline"
                    className="w-full bg-transparent border-red-500/50 text-red-500 hover:bg-red-500/10 hover:border-red-500 uppercase tracking-wider"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                >
                    <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                </Button>

                {/* EL «NO QUIERO RENOVAR» (doc 19-08, «Mi plan y la baja»): «abajo, en pequeño
                    y sin botón naranja. No se esconde, pero tampoco se invita.» Y ABAJO DEL
                    TODO, junto a «Cerrar sesión» (Jesús, 4-09, punto 91): estaba dentro de la
                    tarjeta «Mi plan», pegado al precio y a la fecha de renovación. Mismo
                    enlace, mismo diálogo; solo cambia el sitio. Solo con plan en marcha. */}
                {profile && !planUnpaid && profile.plan && !sinAcceso && !(actuaSinSerAdmin && !noRenueva) && (
                    !noRenueva ? (
                        <p className="text-center">
                            <button type="button" onClick={() => setBajaAbierta(true)}
                                data-testid="no-quiero-renovar"
                                className="text-xs text-foreground/40 hover:text-foreground/70 underline underline-offset-2">
                                No quiero renovar
                            </button>
                        </p>
                    ) : (
                        // CON SUS DOS FECHAS (hoja del 22-10, fila 78): la frase la escribe el
                        // servidor (`core.bajas_pedidas.frase_de_la_baja`).
                        <p className="text-xs text-foreground/50 text-center" data-testid="baja-pedida">
                            {profile?.renovacion?.frase_baja
                                || 'Ya nos dijiste que no quieres renovar: no se te vuelve a cobrar y tienes acceso hasta el final de tu plan.'}
                        </p>
                    )
                )}

                {/* Change Password Dialog: un enlace a su correo, sin «Contraseña actual». */}
                <Dialog open={showPasswordDialog} onOpenChange={o => { setShowPasswordDialog(o); if (!o) setEnlaceEnviado(false); }}>
                    <DialogContent className="bg-card border-input" data-testid="change-password-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-foreground uppercase tracking-wider">Cambiar contraseña</DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-foreground/80" data-testid="pwd-explicacion">
                            {enlaceEnviado
                                ? <>Te hemos mandado un enlace a <b>{user?.email}</b>. Ábrelo para poner la nueva contraseña.</>
                                : <>Te mandamos un enlace a <b>{user?.email}</b> para que pongas la nueva contraseña.</>}
                        </p>
                        <DialogFooter>
                            {enlaceEnviado ? (
                                <Button onClick={() => setShowPasswordDialog(false)} className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white" data-testid="pwd-cerrar">
                                    Entendido
                                </Button>
                            ) : (<>
                                <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="bg-transparent border-input text-foreground">Cancelar</Button>
                                <Button onClick={handleChangePassword} disabled={enviandoEnlace || !user?.email} className="bg-[#FF671F] hover:bg-[#FF671F]/90 text-white" data-testid="pwd-save">
                                    {enviandoEnlace ? 'Enviando...' : 'Enviarme el enlace'}
                                </Button>
                            </>)}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Upgrade Dialog */}
                <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
                    <DialogContent className="bg-card border-input">
                        <DialogHeader>
                            <DialogTitle className="text-foreground uppercase tracking-wider">Mejorar tu plan</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            {profile?.plan !== 'gold' && (
                                <Card className="bg-background border-input cursor-pointer hover:border-yellow-500 transition-colors" onClick={() => toast.info('Funcionalidad próximamente')}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="bg-gradient-to-r from-yellow-500 via-yellow-400 to-yellow-600 text-foreground font-bold px-3 py-1 rounded text-sm uppercase">Gold</span>
                                            <span className="font-bold text-foreground text-xl" style={{ fontFamily: 'Barlow Condensed' }}>149€/ciclo</span>
                                        </div>
                                        <p className="text-sm text-foreground/50">Incluye todo: rutina semanal, reporte quincenal, cardio, audio y suplementación.</p>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)} className="bg-transparent border-input text-foreground">Cancelar</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default ProfilePage;
